import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FileText } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from '@/components/ui/toast'
import { AttachmentPreviewGrid } from '@/components/shared/AttachmentPreviewGrid'
import { FileUploadDropzone } from '@/components/shared/FileUploadDropzone'
import type { AttachmentRef } from '@/components/shared/types'
import { getApiErrorMessage } from '@/services/api'
import { DOCUMENT_FILE_ACCEPT, isDocumentFileExtension } from '@/lib/constants'
import { getFileSizeLimitMb, isFileTooLarge } from '@/lib/uploadValidation'
import {
  useTaskFiles,
  useUploadTaskFile,
  useRemoveTaskFile,
  useRenameTaskFile,
} from '@/hooks/useTasks'
import type { TaskFile } from '@/types'

interface Props {
  taskId: string
  detailPage?: boolean
  hideHeading?: boolean
  readOnly?: boolean
  onUploadReady?: (openUpload: () => void) => void
  onUploadingChange?: (uploading: boolean) => void
}

function toRef(file: TaskFile): AttachmentRef {
  return {
    id: file.id,
    name: file.name ?? file.id,
    url: file.url,
    mime_type: file.mime_type,
    size: file.size,
    source: 'upload',
    created_at: file.created_at,
  }
}

// Task attachments (feedback #70). Detail-page wiring: uploads persist
// immediately, removals delete the persisted file.
export function TaskAttachments({
  taskId,
  detailPage,
  readOnly = false,
  onUploadReady,
  onUploadingChange,
}: Props) {
  const { t } = useTranslation('tasks')
  const { t: tShared } = useTranslation('shared')
  const { data, isLoading } = useTaskFiles(taskId)
  const upload = useUploadTaskFile(taskId)
  const remove = useRemoveTaskFile(taskId)
  const rename = useRenameTaskFile(taskId)
  const [sectionUploading, setSectionUploading] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const isBusy =
    sectionUploading || upload.isPending || remove.isPending || rename.isPending

  useEffect(() => {
    onUploadingChange?.(isBusy)
  }, [isBusy, onUploadingChange])

  useEffect(() => {
    if (readOnly) return
    onUploadReady?.(() => {
      if (!isBusy) fileInputRef.current?.click()
    })
  }, [isBusy, onUploadReady, readOnly])

  const value = useMemo(() => (data?.data ?? []).map(toRef), [data])

  const uploadFiles = async (files: FileList | null) => {
    if (!files?.length || isBusy || readOnly) return

    let uploaded = 0
    setSectionUploading(true)
    try {
      for (const file of Array.from(files)) {
        const extension = file.name.split('.').pop()?.toLowerCase() ?? ''
        if (!isDocumentFileExtension(extension)) {
          toast({
            variant: 'destructive',
            title: t('attachments.unsupportedFormat'),
          })
          continue
        }
        if (isFileTooLarge(file)) {
          toast({
            variant: 'destructive',
            title: tShared('attachFiles.tooLarge', {
              max: getFileSizeLimitMb(),
            }),
          })
          continue
        }

        try {
          await upload.mutateAsync(file)
          uploaded += 1
        } catch (error) {
          toast({
            variant: 'destructive',
            title: getApiErrorMessage(error, t('attachments.uploadError')),
          })
        }
      }

      if (uploaded > 0) {
        toast({ variant: 'success', title: t('attachments.uploadSuccess') })
      }
    } finally {
      setSectionUploading(false)
    }
  }

  const removeFile = async (item: AttachmentRef) => {
    if (!item.id) return
    try {
      await remove.mutateAsync(item.id)
      toast({ variant: 'success', title: t('attachments.removeSuccess') })
    } catch (error) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(error, t('attachments.removeError')),
      })
    }
  }

  const renameFile = async (item: AttachmentRef, name: string) => {
    if (!item.id || readOnly) return
    await rename.mutateAsync({ fileId: item.id, name })
    toast({ variant: 'success', title: t('attachments.renameSuccess') })
  }

  if (isLoading) {
    return <Skeleton className='h-24 w-full' />
  }

  if (!detailPage) {
    return null
  }

  return (
    <div className='flex flex-col gap-4'>
      <input
        ref={fileInputRef}
        type='file'
        multiple
        accept={DOCUMENT_FILE_ACCEPT}
        className='hidden'
        disabled={isBusy || readOnly}
        onChange={event => {
          void uploadFiles(event.target.files)
          event.target.value = ''
        }}
      />

      {!readOnly && (
        <FileUploadDropzone
          onClick={() => fileInputRef.current?.click()}
          disabled={isBusy}
          primaryText={tShared('attachFiles.uploadHintPrimary')}
          secondaryText={tShared('attachFiles.uploadHintFormats')}
          loading={isBusy}
          loadingText={t('attachments.uploading')}
          dragActive={dragActive}
          onDragOver={event => {
            event.preventDefault()
            setDragActive(true)
          }}
          onDragLeave={event => {
            event.preventDefault()
            setDragActive(false)
          }}
          onDrop={event => {
            event.preventDefault()
            setDragActive(false)
            void uploadFiles(event.dataTransfer.files)
          }}
        />
      )}

      {value.length === 0 ? (
        <div className='flex min-h-11 items-center gap-2 rounded-xl border border-[#e7e9ef] bg-[#f4f5f8] p-3 text-sm font-medium leading-[1.4] text-[#3d434e]'>
          <FileText className='size-4 shrink-0 text-[#6c7285]' />
          <span>{t('attachments.empty')}</span>
        </div>
      ) : (
        <AttachmentPreviewGrid
          items={value}
          removeLabel={tShared('attachFiles.remove')}
          onRemove={readOnly ? undefined : removeFile}
          onRename={readOnly ? undefined : renameFile}
          disabled={isBusy}
          onItemClick={item => {
            if (item.url) window.open(item.url, '_blank', 'noopener')
          }}
          itemClickLabel={tShared('attachFiles.view')}
        />
      )}
    </div>
  )
}

export default TaskAttachments
